import { useState, useEffect, useRef } from 'react';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatOpen, setIsMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between h-16 md:h-18">
          <a
            href="/"
            className="flex items-center gap-2 text-foreground-950 hover:text-primary-600 transition-colors duration-200 cursor-pointer"
          >
            <div className="w-8 h-8 flex items-center justify-center bg-primary-500 rounded-lg">
              <i className="ri-database-2-line text-lg text-white"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-semibold whitespace-nowrap">
              Kakobuy<span className="text-primary-600">Polska</span>
            </span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            <a
              href="/"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Strona Główna
            </a>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Kategorie
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-72 bg-background-50 border border-background-200/80 rounded-xl shadow-lg py-2 max-h-96 overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-all duration-150 cursor-pointer whitespace-nowrap"
                    >
                      <div className="w-7 h-7 flex items-center justify-center bg-background-100 rounded-md">
                        <i className={`${cat.icon} text-sm text-primary-600`}></i>
                      </div>
                      {cat.namePl}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Przewodnik Zakupowy
            </a>
            <a
              href="#bezpieczenstwo"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Bezpieczeństwo
            </a>
            <a
              href="#blog"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Blog
            </a>
            <a
              href="#faq"
              className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              FAQ
            </a>
          </div>

          <div className="hidden md:flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold text-background-50 bg-primary-600 hover:bg-primary-700 rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer"
            >
              Kakobuy Spreadsheet
            </a>
          </div>

          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 hover:text-foreground-950 rounded-lg cursor-pointer"
          >
            <i className={`text-xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-background-200/70 bg-background-50 max-h-[calc(100vh-64px)] overflow-y-auto">
            <div className="flex flex-col py-3">
              <a
                href="/"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-950 hover:bg-primary-50 rounded-lg transition-all duration-200 cursor-pointer mx-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center bg-primary-100 rounded-lg">
                  <i className="ri-home-4-line text-sm text-primary-600"></i>
                </div>
                Strona Główna
              </a>

              <a
                href="/artykul/kakobuy-spreadsheet-kompletny-przewodnik"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-all duration-200 cursor-pointer mx-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-lg">
                  <i className="ri-book-open-line text-sm text-accent-600"></i>
                </div>
                Przewodnik Zakupowy
              </a>

              <div className="my-2 mx-3 border-t border-background-200/70"></div>

              <button
                onClick={() => setIsMobileCatOpen(!isMobileCatOpen)}
                className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-all duration-200 cursor-pointer mx-3"
              >
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-lg">
                    <i className="ri-grid-line text-sm text-secondary-600"></i>
                  </div>
                  Kategorie produktów ({categories.length})
                </div>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${isMobileCatOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isMobileCatOpen && (
                <div className="grid grid-cols-2 gap-1 px-3 mt-1 mb-2">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2 px-3 py-2.5 text-xs font-medium text-foreground-600 hover:text-foreground-950 hover:bg-background-100 rounded-lg transition-all duration-150 cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center bg-background-100 rounded-md">
                        <i className={`${cat.icon} text-[10px] text-primary-600`}></i>
                      </div>
                      <span className="truncate">{cat.namePl}</span>
                    </a>
                  ))}
                </div>
              )}

              <div className="my-2 mx-3 border-t border-background-200/70"></div>

              <a
                href="#bezpieczenstwo"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-all duration-200 cursor-pointer mx-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center bg-accent-100 rounded-lg">
                  <i className="ri-shield-check-line text-sm text-accent-600"></i>
                </div>
                Bezpieczeństwo
              </a>

              <a
                href="#blog"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-all duration-200 cursor-pointer mx-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-lg">
                  <i className="ri-article-line text-sm text-secondary-600"></i>
                </div>
                Blog
              </a>

              <a
                href="#faq"
                className="flex items-center gap-3 px-4 py-3 text-sm font-medium text-foreground-700 hover:bg-primary-50 rounded-lg transition-all duration-200 cursor-pointer mx-3"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center bg-secondary-100 rounded-lg">
                  <i className="ri-question-line text-sm text-secondary-600"></i>
                </div>
                FAQ
              </a>

              <div className="mt-3 mb-1 mx-3">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-4 py-3 text-sm font-semibold text-background-50 bg-primary-600 hover:bg-primary-700 rounded-lg transition-all duration-200 cursor-pointer"
                >
                  Kakobuy Spreadsheet
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}